/**
 * services/EmailService.ts
 * Transactional email service for company-to-user communications
 *
 * Sends emails FROM company (support@enfomentis.com) TO users for:
 * - Beta application approvals/rejections
 * - User invitation and activation emails
 * - Password reset notifications
 * - Administrative communications
 *
 * Development: Console logging stub
 * Production: Supabase Edge Function → Resend API
 */

import { emailTemplateService } from './EmailTemplateService';
import { emailCategoryService } from './EmailCategoryService';
import { communicationService } from './CommunicationService';
import { betaApplicationService } from './BetaApplicationService';
import { supabase } from '@/lib/supabase';
import type {
  EmailSendRequest,
  EmailSendResponse,
  EmailTemplateType,
  EmailTemplateVariables,
  BulkEmailRequest,
  BulkEmailResponse,
  BulkEmailProgress
} from '@/types/email';
import type { EmailCategory, EmailCategoryInsert } from '@/types/supabase';

/**
 * Email Service
 *
 * Provides email sending capabilities with template rendering and variable substitution.
 * Development: Console logging for local testing
 * Production: Supabase Edge Function calls Resend API server-side
 */
export class EmailService {
  private isProduction: boolean = false;
  private readonly edgeFunctionUrl: string;

  constructor() {
    // Check Supabase URL to determine if we're in production mode
    const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'];
    this.edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-email`;

    // Production mode if we have a real Supabase URL (not localhost)
    this.isProduction = Boolean(supabaseUrl && !supabaseUrl.includes('localhost') && !supabaseUrl.includes('127.0.0.1'));

    if (this.isProduction) {
      console.log('📧 EmailService initialized with Edge Function (production mode)');
    } else {
      console.log('📧 EmailService initialized with development stub');
    }
  }

  // ===== MAIN EMAIL SENDING METHODS =====

  /**
   * Send email using template
   *
   * @param templateType - Type of email template to use
   * @param variables - Template variables for substitution
   * @param recipientEmail - Email address to send to
   * @param sentBy - Admin username sending the email
   * @param applicationId - Beta application ID for tracking
   * @returns Promise resolving to send response
   */
  async sendTemplateEmail(
    templateType: EmailTemplateType,
    variables: EmailTemplateVariables,
    recipientEmail: string,
    sentBy: string,
    applicationId: string
  ): Promise<EmailSendResponse> {
    try {
      console.log(`📧 Preparing ${templateType} email for ${recipientEmail}...`);

      // Get email template
      const template = await emailTemplateService.getByType(templateType);
      if (!template) {
        throw new Error(`Email template not found: ${templateType}`);
      }

      // Render template with variables
      const renderedContent = this.renderTemplate(template.content, variables);
      const renderedSubject = this.renderTemplate(template.subject, variables);

      // Create send request
      const sendRequest: EmailSendRequest = {
        to: recipientEmail,
        subject: renderedSubject,
        content: renderedContent,
        template_type: templateType,
        application_id: applicationId,
        sent_by: sentBy
      };

      // Send email
      const result = await this.sendEmail(sendRequest);

      console.log(`✅ ${templateType} email sent successfully to ${recipientEmail}`);
      return result;
    } catch (error) {
      console.error(`❌ Failed to send ${templateType} email:`, error);
      throw new Error(`Failed to send ${templateType} email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Send custom email (not using template)
   *
   * @param sendRequest - Complete email send request
   * @returns Promise resolving to send response
   */
  async sendCustomEmail(sendRequest: EmailSendRequest): Promise<EmailSendResponse> {
    try {
      console.log(`📧 Sending custom email to ${sendRequest.to}...`);

      const result = await this.sendEmail(sendRequest);

      console.log(`✅ Custom email sent successfully to ${sendRequest.to}`);
      return result;
    } catch (error) {
      console.error('❌ Failed to send custom email:', error);
      throw new Error(`Failed to send custom email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ===== TEMPLATE RENDERING METHODS =====

  /**
   * Render template with variable substitution
   *
   * @param template - Template string with {{variables}}
   * @param variables - Variables to substitute
   * @returns Rendered template string
   */
  renderTemplate(template: string, variables: EmailTemplateVariables): string {
    let rendered = template;

    // Required variables
    rendered = rendered.replace(/\{\{applicant_name\}\}/g, variables.applicant_name);
    rendered = rendered.replace(/\{\{application_date\}\}/g, variables.application_date);
    rendered = rendered.replace(/\{\{decision_date\}\}/g, variables.decision_date);
    rendered = rendered.replace(/\{\{admin_name\}\}/g, variables.admin_name);

    // Optional variables
    if (variables.admin_notes) {
      rendered = rendered.replace(/\{\{admin_notes\}\}/g, variables.admin_notes);
    } else {
      // Remove admin_notes placeholders if no notes provided
      rendered = rendered.replace(/\{\{admin_notes\}\}/g, '');
    }

    // Clean up any remaining double spaces or empty lines
    rendered = rendered.replace(/\n\s*\n\s*\n/g, '\n\n'); // Remove triple+ newlines
    rendered = rendered.replace(/  +/g, ' '); // Remove multiple spaces

    return rendered.trim();
  }

  /**
   * Extract variables from template content
   *
   * @param content - Template content to analyze
   * @returns Array of variable names found
   */
  extractTemplateVariables(content: string): string[] {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(content)) !== null) {
      const variableName = match[1]?.trim();
      if (variableName && !variables.includes(variableName)) {
        variables.push(variableName);
      }
    }

    return variables;
  }

  // ===== CORE EMAIL SENDING METHODS =====

  /**
   * Core email sending method
   * Development: Console logging stub
   * Production: OAuth2 or SMTP integration
   *
   * @param sendRequest - Email send request
   * @returns Promise resolving to send response
   */
  private async sendEmail(sendRequest: EmailSendRequest): Promise<EmailSendResponse> {
    // TODO: [CONTRACTOR] Replace console logging with real email delivery - configure production email service and environment detection
    if (this.isProduction) {
      return this.sendEmailProduction(sendRequest);
    } else {
      return this.sendEmailDevelopment(sendRequest);
    }
  }

  /**
   * Development email sending (console logging)
   *
   * @param sendRequest - Email send request
   * @returns Promise resolving to mock send response
   */
  private async sendEmailDevelopment(sendRequest: EmailSendRequest): Promise<EmailSendResponse> {
    console.log('\n📧 ===== EMAIL SENT (DEVELOPMENT) =====');
    console.log('📧 To:', sendRequest.to);
    console.log('📧 Subject:', sendRequest.subject);
    console.log('📧 Template Type:', sendRequest.template_type);
    console.log('📧 Application ID:', sendRequest.application_id);
    console.log('📧 Sent By:', sendRequest.sent_by);
    console.log('📧 Content:');
    console.log('📧 ----------------------------------------');
    console.log(sendRequest.content);
    console.log('📧 ========================================\n');

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 100));

    return {
      success: true,
      message_id: `dev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sent_at: new Date().toISOString()
    };
  }

  /**
   * Production email sending via Supabase Edge Function → Resend API
   *
   * @param sendRequest - Email send request
   * @returns Promise resolving to send response
   */
  private async sendEmailProduction(sendRequest: EmailSendRequest): Promise<EmailSendResponse> {
    try {
      console.log(`📧 Sending email via Edge Function to ${sendRequest.to}...`);

      // Use Supabase client's functions.invoke() which handles authentication properly
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: sendRequest.to,
          subject: sendRequest.subject,
          content: sendRequest.content,
          template_type: sendRequest.template_type,
          application_id: sendRequest.application_id,
          user_id: sendRequest.user_id,
          sent_by: sendRequest.sent_by
        }
      });

      if (error) {
        console.error('❌ Edge Function error:', error);
        return {
          success: false,
          error: error.message || 'Failed to send email via Edge Function'
        };
      }

      const result = data as EmailSendResponse;

      if (!result.success) {
        console.error('❌ Edge Function error:', result.error);
        return {
          success: false,
          error: result.error || 'Failed to send email via Edge Function'
        };
      }

      console.log(`✅ Email sent successfully via Edge Function. Message ID: ${result.message_id}`);

      return {
        success: true,
        message_id: result.message_id || `edge_${Date.now()}`,
        sent_at: result.sent_at || new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Failed to send email via Edge Function:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // ===== CONFIGURATION METHODS =====

  /**
   * Test email configuration
   *
   * @returns Promise resolving to test result
   */
  async testEmailConfiguration(): Promise<{ success: boolean; message: string }> {
    if (this.isProduction) {
      console.log('🧪 Testing Edge Function configuration...');

      try {
        // Edge Function is configured via Supabase
        console.log('✅ Edge Function URL is configured:', this.edgeFunctionUrl);
        return {
          success: true,
          message: 'Email Edge Function is configured correctly. Domain: enfomentis.app'
        };
      } catch (error) {
        console.error('❌ Edge Function configuration test failed:', error);
        return {
          success: false,
          message: error instanceof Error ? error.message : 'Edge Function configuration test failed'
        };
      }
    } else {
      console.log('🧪 Email configuration test (development mode)');
      console.log('✅ Development stub is working correctly');

      return {
        success: true,
        message: 'Development email service is configured correctly'
      };
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Format email address with name
   *
   * @param email - Email address
   * @param name - Display name (optional)
   * @returns Formatted email address
   */
  formatEmailAddress(email: string, name?: string): string {
    if (name) {
      return `"${name}" <${email}>`;
    }
    return email;
  }

  /**
   * Validate email address format
   *
   * @param email - Email address to validate
   * @returns True if valid email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get email sending statistics
   *
   * @returns Promise resolving to email statistics
   */
  async getEmailStats(): Promise<{
    total_sent: number;
    sent_today: number;
    failed_today: number;
    last_sent_at: string | null;
  }> {
    // TODO: [CONTRACTOR] Implement email analytics tracking - database integration for send/failure statistics and reporting
    console.log('📊 Email stats (development mode - returning mock data)');

    return {
      total_sent: 0,
      sent_today: 0,
      failed_today: 0,
      last_sent_at: null
    };
  }

  // ===== BULK EMAIL METHODS =====

  /**
   * Send bulk emails to multiple beta applications
   *
   * @param bulkRequest - Bulk email request
   * @param progressCallback - Optional callback for progress updates
   * @returns Promise resolving to bulk email response
   */
  async sendBulkEmails(
    bulkRequest: BulkEmailRequest,
    progressCallback?: (progress: BulkEmailProgress) => void
  ): Promise<BulkEmailResponse> {
    const { template_type, application_ids, sent_by, custom_variables } = bulkRequest;

    // Initialize progress tracking
    const progress: BulkEmailProgress = {
      total: application_ids.length,
      processed: 0,
      successful: 0,
      failed: 0,
      status: 'preparing'
    };

    const response: BulkEmailResponse = {
      total_requested: application_ids.length,
      sent_count: 0,
      failed_count: 0,
      successful_sends: [],
      failed_sends: [],
      operation_status: 'completed'
    };

    try {
      console.log(`📧 Starting bulk ${template_type} email operation for ${application_ids.length} applications`);

      // Get email template
      const template = await emailTemplateService.getByType(template_type);
      if (!template) {
        throw new Error(`Email template not found: ${template_type}`);
      }

      // Update progress
      progress.status = 'sending';
      progressCallback?.(progress);

      // Process each application
      for (const applicationId of application_ids) {
        try {
          // Get application details
          const application = await betaApplicationService.get(applicationId);
          if (!application) {
            response.failed_sends.push({
              application_id: applicationId,
              email: 'unknown',
              error: 'Application not found'
            });
            response.failed_count++;
            progress.failed++;
            continue;
          }

          // Prepare template variables
          const variables: EmailTemplateVariables = {
            applicant_name: application.fullName,
            application_date: new Date(application.createdAt).toLocaleDateString(),
            decision_date: new Date().toLocaleDateString(),
            admin_name: sent_by,
            ...custom_variables
          };

          // Send email
          const emailResponse = await this.sendTemplateEmail(
            template_type,
            variables,
            application.email,
            sent_by,
            applicationId
          );

          if (emailResponse.success) {
            // Record successful send
            await communicationService.recordEmailSent(
              'beta_application',
              applicationId,
              application.email,
              template,
              variables,
              sent_by,
              emailResponse.message_id
            );

            response.successful_sends.push({
              application_id: applicationId,
              email: application.email,
              message_id: emailResponse.message_id || '',
              sent_at: emailResponse.sent_at || new Date().toISOString()
            });
            response.sent_count++;
            progress.successful++;
          } else {
            throw new Error(emailResponse.error || 'Unknown email error');
          }

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(`❌ Failed to send ${template_type} email to application ${applicationId}:`, error);

          // Get application for error logging
          const application = await betaApplicationService.get(applicationId);
          const applicantEmail = application?.email || 'unknown';

          // Record failed send
          if (application) {
            const variables: EmailTemplateVariables = {
              applicant_name: application.fullName,
              application_date: new Date(application.createdAt).toLocaleDateString(),
              decision_date: new Date().toLocaleDateString(),
              admin_name: sent_by,
              ...custom_variables
            };

            await communicationService.recordEmailFailed(
              'beta_application',
              applicationId,
              application.email,
              template,
              variables,
              sent_by,
              errorMessage
            );
          }

          response.failed_sends.push({
            application_id: applicationId,
            email: applicantEmail,
            error: errorMessage
          });
          response.failed_count++;
          progress.failed++;
        }

        // Update progress
        progress.processed++;
        progressCallback?.(progress);

        // Small delay between sends to avoid overwhelming email service
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Determine operation status
      if (response.failed_count === 0) {
        response.operation_status = 'completed';
        progress.status = 'completed';
      } else if (response.sent_count > 0) {
        response.operation_status = 'partial_failure';
        progress.status = 'completed';
      } else {
        response.operation_status = 'failed';
        progress.status = 'failed';
      }

      // Final progress update
      progressCallback?.(progress);

      console.log(`✅ Bulk email operation completed: ${response.sent_count} sent, ${response.failed_count} failed`);
      return response;

    } catch (error) {
      console.error('❌ Bulk email operation failed:', error);
      progress.status = 'failed';
      progress.error = error instanceof Error ? error.message : 'Unknown error';
      progressCallback?.(progress);

      response.operation_status = 'failed';
      return response;
    }
  }

  // ===== EMAIL CATEGORY MANAGEMENT METHODS =====

  /**
   * Get all email categories
   *
   * @returns Promise resolving to array of email categories
   */
  async getEmailCategories(): Promise<EmailCategory[]> {
    return await emailCategoryService.list();
  }

  /**
   * Get email category by name
   *
   * @param name - Category name
   * @returns Promise resolving to category or null
   */
  async getEmailCategoryByName(name: string): Promise<EmailCategory | null> {
    return await emailCategoryService.getByName(name);
  }

  /**
   * Create email category
   *
   * @param categoryData - Category data
   * @returns Promise resolving to created category
   */
  async createEmailCategory(categoryData: EmailCategoryInsert): Promise<EmailCategory> {
    return await emailCategoryService.create(categoryData);
  }
}

// Export singleton instance
export const emailService = new EmailService();